// MACHINERY V2 (Stroje) — delete-blocker code -> user-facing message (MDEL-FE).
//
// Spec: MACHINE_DELETE_SPEC.md §4.1 (blocker vocabulary) + §8 (i18n keys).
//
// >>> THE POINT OF THIS FILE IS THAT THE USER IS NEVER TOLD "cannot delete". <<<
// The server enumerates EIGHT closed reasons; collapsing them into one generic
// sentence throws away the only thing that makes the refusal actionable — a
// person who is told "it has service reports (3)" knows what to do next, a
// person told "cannot delete" files a bug. So every code gets its own clause,
// and an unrecognised code falls through to the raw code rather than to
// silence.
//
// VerificationUnavailable IS NOT A FAILURE MESSAGE AND IT IS NOT "no blockers".
// It means a peer check (Servicing / Maintenance / MachineGateway) could not be
// completed, so the server REFUSED rather than guessed — including the case
// where the peer's base-URL config key is missing from the deployed
// appsettings, which is a permanent state, not a blip (spec §4.4 ruling:
// unconfigured is treated exactly like unreachable). Its wording therefore says
// "we could not check X, so deletion was refused" and points at an
// administrator, so a config gap does not read to the owner as a dead button.
// This is the one place where the shipped English deviates from the spec §8
// table's "eligibility could not be verified - try again later": that phrasing
// promises transience the unconfigured case cannot keep.
//
// NO SERVER BEHAVIOUR IS ASSERTED HERE. At the time of writing the Machinery
// endpoints are being implemented on module/machinery in GoToStockServer-
// machinery and do not exist yet, so nothing below cites a controller line —
// it is coded to the frozen contract in the spec, and the mapping is
// deliberately tolerant of a payload that does not match it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import type useTranslation from "../../../../shared/hooks/useTranslation";
import {
  MachineDeleteBlocker,
  MachineDeleteBlockerCode,
} from "../../api/customerMachineryApi";

/** The hook's own translate, aliased rather than re-declared as
 *  `(key: string) => string`: i18next's TFunction is NOT assignable to a
 *  hand-written two-argument signature under strictFunctionTypes, which is a
 *  compile error at every call site. Same alias v2/readiness/CapabilityBadge.tsx
 *  uses (its `Translate`). `import type` keeps this a pure util — no hook
 *  module is pulled into its runtime import graph. */
export type BlockerTranslate = ReturnType<typeof useTranslation>["translate"];

/** Exhaustive by construction: a ninth blocker code added to the union in
 *  customerMachineryApi.ts fails this object at compile time. */
const BLOCKER_KEYS: Record<MachineDeleteBlockerCode, string> = {
  NotDecommissioned: "myMachinery:v2.delete.blocker.notDecommissioned",
  HasServiceReports: "myMachinery:v2.delete.blocker.serviceReports",
  HasDocuments: "myMachinery:v2.delete.blocker.documents",
  HasNameplate: "myMachinery:v2.delete.blocker.nameplate",
  HasServiceRequests: "myMachinery:v2.delete.blocker.serviceRequests",
  HasWorkOrders: "myMachinery:v2.delete.blocker.workOrders",
  HasGatewayConnection: "myMachinery:v2.delete.blocker.gateway",
  // Overridden below when the payload names a service — see unverifiedClause.
  VerificationUnavailable: "myMachinery:v2.delete.blocker.unverified",
};

/** Peer service -> a phrase a machine-shop owner recognises. "MachineGateway"
 *  is an internal service name and must never reach a user — an UNRECOGNISED
 *  value therefore renders the generic `service.unknown`, never the raw
 *  identifier. (Review finding 6: the previous `: raw` fallback leaked exactly
 *  the string the line above forbids, on any casing drift or fourth peer.) */
const SERVICE_KEYS: Record<string, string> = {
  Servicing: "myMachinery:v2.delete.service.servicing",
  Maintenance: "myMachinery:v2.delete.service.maintenance",
  MachineGateway: "myMachinery:v2.delete.service.machineGateway",
};

/**
 * CASE-INSENSITIVE INDEX (review finding 4).
 *
 * The lookups below key on values the server sends. A .NET controller that
 * serialises its enum through a globally-registered
 * JsonStringEnumConverter(JsonNamingPolicy.CamelCase) emits
 * `hasServiceReports`, not `HasServiceReports` — which an exact-case map misses
 * for EVERY code, leaving the owner with raw identifiers, no counts and no
 * remedy. The TypeScript union is a COMPILE-time guard and cannot see a runtime
 * value mismatch at all, so both sides of the lookup are folded to lower case.
 * (Whether the real serialiser does this is unknown — the controller does not
 * exist yet. This is the belt; the reconciliation pass against the shipped
 * serialiser is the braces.)
 */
const lowerIndex = (source: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const key of Object.keys(source)) out[key.toLowerCase()] = source[key];
  return out;
};

const BLOCKER_KEYS_CI = lowerIndex(BLOCKER_KEYS);
const SERVICE_KEYS_CI = lowerIndex(SERVICE_KEYS);

const norm = (value: string | undefined): string =>
  typeof value === "string" ? value.toLowerCase() : "";

const unverifiedClause = (
  blocker: MachineDeleteBlocker,
  translate: BlockerTranslate
): string => {
  const raw = norm(blocker.service);
  if (!raw) return translate("myMachinery:v2.delete.blocker.unverified");
  const serviceKey = SERVICE_KEYS_CI[raw];
  return translate("myMachinery:v2.delete.blocker.unverifiedService", {
    service: translate(serviceKey || "myMachinery:v2.delete.service.unknown"),
  });
};

/** One blocker -> one lowercase clause, ready to sit after `blockedIntro`. */
export const describeBlocker = (
  blocker: MachineDeleteBlocker,
  translate: BlockerTranslate
): string => {
  if (!blocker || !blocker.code) return "";
  const code = norm(blocker.code);
  if (code === "verificationunavailable") {
    return unverifiedClause(blocker, translate);
  }
  // A payload carrying a code this build has never heard of lands here as
  // undefined rather than as an empty message, so the user still gets something
  // reportable instead of a bare colon.
  const key: string | undefined = BLOCKER_KEYS_CI[code];
  if (!key) return String(blocker.code);
  // `count` is interpolated unconditionally — i18next simply ignores a variable
  // the string does not reference, so the countless clauses are unaffected.
  return translate(key, { count: blocker.count });
};

/**
 * The full refusal sentence for a toast: intro + every clause the server sent.
 *
 * Defensive on an empty/missing list because `canDelete:false` with no blockers
 * is a contract violation we must still render as something a user can report,
 * not as a bare colon.
 */
export const buildBlockedMessage = (
  blockers: MachineDeleteBlocker[] | undefined,
  translate: BlockerTranslate
): string => {
  const intro = translate("myMachinery:v2.delete.blockedIntro");
  const clauses = (blockers || [])
    .map((b) => describeBlocker(b, translate))
    .filter((c) => c.length > 0);
  if (clauses.length === 0) {
    return intro + " " + translate("myMachinery:v2.delete.blockedUnknown");
  }
  return intro + " " + clauses.join("; ") + ".";
};

export default buildBlockedMessage;
